/**
 * ─── Payment Module · Types & Contracts ─────────────────────────────────────
 *
 * Contrats du module de paiement. Le reste de l'application n'importe JAMAIS
 * un provider directement : elle passe par `PaymentService` (services/) et
 * `usePaymentGate` (hooks/). Chaque fournisseur (FlexPay, M-Pesa, Mock)
 * implémente `PaymentProvider` et peut être remplacé sans toucher au reste.
 */

// ─── Identifiants ────────────────────────────────────────────────────────────

export enum PaymentProviderId {
  FLEXPAY = 'flexpay',
  MPESA = 'mpesa',
  ORANGE = 'orange',
  MOCK = 'mock',
}

/**
 * Canal d'ouverture de l'application — détecté depuis le préfixe d'URL et
 * conservé partout via le `basename` du routeur. Il détermine les moyens de
 * paiement proposés (voir `CHANNEL_OPTIONS` dans config/defaults.ts).
 */
export enum AppChannel {
  /** Super App Maxit (Orange) → paiement Orange Money uniquement. Base `/maxit`. */
  MAXIT = 'maxit',
  /** Super App M-Pesa (Vodacom) → carte Visa M-Pesa + M-Pesa. Base `/mpesa`. */
  MPESA_SUPER_APP = 'mpesa_super_app',
  /** Navigateur, PWA, stores (monde + RDC) → FlexPay (carte + mobile money). */
  WEB = 'web',
}

/**
 * Moyen de paiement présenté à l'utilisateur. Le provider effectif dépend du
 * couple (canal, méthode) — voir `CHANNEL_OPTIONS`. Chaque méthode peut être
 * activée/désactivée individuellement en admin (maintenance).
 */
export enum PaymentMethod {
  /** Carte Visa / Mastercard — FlexPay (web) ou carte Visa M-Pesa (Super App). */
  BANK_CARD = 'bank_card',
  /** Mobile Money RDC via FlexPay (Airtel, Orange, M-Pesa, Africell…). */
  MOBILE_MONEY = 'mobile_money',
  /** Orange Money dans la Super App Maxit. */
  ORANGE_MONEY = 'orange_money',
  /** M-Pesa (push/PIN) dans la Super App M-Pesa. */
  MPESA = 'mpesa',
}

// ─── États d'un paiement (machine d'états) ──────────────────────────────────

export enum PaymentStatus {
  IDLE = 'idle',
  /** Requête d'initiation en cours (génération de l'URL de paiement…). */
  LOADING = 'loading',
  /** Redirection vers la page de paiement hébergée (flow FlexPay V2). */
  REDIRECTING = 'redirecting',
  /** Push envoyé / transaction initiée, en attente d'action utilisateur. */
  PENDING = 'pending',
  /** Retour du provider reçu, vérification du statut réel (check tx). */
  VERIFYING = 'verifying',
  SUCCESS = 'success',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  /** Session de paiement ou vérification qui n'aboutit pas dans les délais. */
  EXPIRED = 'expired',
  NETWORK_ERROR = 'network_error',
}

/** Événements de la machine d'états. */
export enum PaymentEvent {
  INITIATE = 'INITIATE',
  REDIRECT = 'REDIRECT',
  PUSH_SENT = 'PUSH_SENT',
  RETURNED_APPROVED = 'RETURNED_APPROVED',
  RETURNED_CANCELLED = 'RETURNED_CANCELLED',
  RETURNED_DECLINED = 'RETURNED_DECLINED',
  VERIFY = 'VERIFY',
  VERIFIED_OK = 'VERIFIED_OK',
  VERIFIED_KO = 'VERIFIED_KO',
  TIMEOUT = 'TIMEOUT',
  NETWORK_FAIL = 'NETWORK_FAIL',
  CANCEL = 'CANCEL',
  RETRY = 'RETRY',
  RESET = 'RESET',
}

// ─── Plans & abonnements ─────────────────────────────────────────────────────

export type Currency = 'USD' | 'CDF';

/**
 * Droits CONFIGURABLES d'un plan — la source de vérité des restrictions
 * appliquées côté client (et, plus tard, côté serveur). `null` = illimité.
 * L'affichage marketing des avantages est DÉRIVÉ de ces valeurs : ce que le
 * plan promet est exactement ce que le code applique.
 */
export interface PlanEntitlements {
  /** Sessions de pratique par jour — null = illimité. */
  dailyPracticeLimit: number | null;
  /** Simulations complètes par jour — null = illimité. */
  dailySimulationLimit: number | null;
}

export const UNLIMITED_ENTITLEMENTS: PlanEntitlements = {
  dailyPracticeLimit: null,
  dailySimulationLimit: null,
};

export interface PaymentPlan {
  id: string;
  name: string;
  description: string;
  /** Prix mensuel dans `currency`. */
  price: number;
  currency: Currency;
  /** Durée en jours d'un cycle (30 par défaut pour un abonnement mensuel). */
  periodDays: number;
  /** Droits réels du plan (restrictions appliquées par le QuotaGate). */
  entitlements: PlanEntitlements;
  /** Avantages marketing ADDITIONNELS (les limites sont affichées d'office). */
  features: string[];
  /** Mise en avant dans l'écran de choix. */
  highlighted?: boolean;
  isActive: boolean;
}

export enum SubscriptionStatus {
  NONE = 'none',
  ACTIVE = 'active',
  EXPIRED = 'expired',
}

export interface Subscription {
  planId: string;
  status: SubscriptionStatus;
  /** ISO date de début du cycle courant. */
  startedAt: string;
  /** ISO date de fin du cycle courant. */
  expiresAt: string;
  /** Provider utilisé pour le dernier paiement. */
  providerId: PaymentProviderId;
  method: PaymentMethod;
  /** Masque du moyen de paiement, ex: "Visa •••• 4242" ou "+243 •• ••• 812". */
  paymentMethodLabel: string | null;
}

// ─── Transactions & factures ─────────────────────────────────────────────────

export interface PaymentTransaction {
  /** Référence générée côté app (équivalent `reference` FlexPay). */
  reference: string;
  /** Référence générée par le provider (équivalent `orderNumber`). */
  providerOrderNumber: string | null;
  planId: string;
  amount: number;
  currency: Currency;
  providerId: PaymentProviderId;
  method: PaymentMethod;
  status: PaymentStatus;
  createdAt: string;
  completedAt: string | null;
  /** Message d'erreur lisible en cas d'échec. */
  failureReason: string | null;
}

export interface Invoice {
  id: string;
  transactionReference: string;
  planId: string;
  planName: string;
  amount: number;
  currency: Currency;
  issuedAt: string;
  periodStart: string;
  periodEnd: string;
  customerName: string;
  customerEmail: string;
}

// ─── Contrat Provider ────────────────────────────────────────────────────────

export interface ProviderCapabilities {
  methods: PaymentMethod[];
  currencies: Currency[];
  /** Le provider utilise une page de paiement hébergée (redirection). */
  usesRedirect: boolean;
}

/**
 * Une option de paiement proposée sur un canal donné : la méthode affichée à
 * l'utilisateur et le provider qui l'exécute. La MÊME méthode peut router vers
 * des providers différents selon le canal (ex: BANK_CARD → FlexPay sur le web,
 * → M-Pesa dans la Super App M-Pesa). Voir `CHANNEL_OPTIONS`.
 */
export interface ChannelOption {
  method: PaymentMethod;
  provider: PaymentProviderId;
}

/**
 * Clé stable d'une OPTION de paiement (couple moyen × fournisseur). C'est
 * l'unité du kill-switch admin : la carte bancaire via FlexPay et la carte
 * bancaire via M-Pesa sont deux options distinctes, activables séparément.
 */
export function paymentOptionKey(
  method: PaymentMethod,
  provider: PaymentProviderId,
): string {
  return `${method}__${provider}`;
}

/** Demande d'initiation d'un paiement, indépendante du provider. */
export interface PaymentRequest {
  reference: string;
  plan: PaymentPlan;
  method: PaymentMethod;
  user: { id?: string; name: string; email: string };
  /** Numéro du payeur — requis pour le mobile money (push). */
  phone?: string;
  /**
   * URLs de retour internes de l'app (le backend les transmettra au provider
   * en tant que approve_url / cancel_url / decline_url pour FlexPay).
   */
  returnUrls: {
    approve: string;
    cancel: string;
    decline: string;
  };
}

/** Résultat d'une initiation — dicte la suite du flow côté UI. */
export type PaymentInitiation =
  | {
      /** Rediriger le navigateur vers une page de paiement hébergée. */
      kind: 'redirect';
      url: string;
      orderNumber: string;
    }
  | {
      /** Push mobile envoyé : afficher l'écran d'attente + polling. */
      kind: 'push_sent';
      orderNumber: string;
      instructions: string;
    }
  | {
      /** Échec immédiat de l'initiation. */
      kind: 'error';
      message: string;
      retriable: boolean;
    };

export interface PaymentVerification {
  status: 'success' | 'failed' | 'pending' | 'not_found';
  transaction: {
    reference: string;
    orderNumber: string;
    amount: number;
    currency: Currency;
  } | null;
  message: string;
}

/** Paramètres extraits de l'URL de retour du provider. */
export interface PaymentReturn {
  outcome: 'approved' | 'cancelled' | 'declined';
  reference: string | null;
  orderNumber: string | null;
}

/**
 * Contrat que chaque fournisseur de paiement doit implémenter.
 *
 * IMPORTANT : les implémentations ne font AUCUN appel HTTP direct aux
 * fournisseurs. Elles parleront au backend de l'app (qui, lui, parlera à
 * FlexPay / M-Pesa). Tant que le backend n'existe pas, seul MockProvider
 * est fonctionnel ; FlexPayProvider et MpesaProvider lèvent une erreur
 * explicite "non branché".
 */
export interface PaymentProvider {
  readonly id: PaymentProviderId;
  readonly displayName: string;
  readonly capabilities: ProviderCapabilities;

  /** Le provider peut-il servir ce canal ? (ex: M-Pesa ↔ Super App). */
  supportsChannel(channel: AppChannel): boolean;

  /** Démarre un paiement. Équivalent FlexPay V2 "générer l'URL de paiement". */
  initiate(request: PaymentRequest): Promise<PaymentInitiation>;

  /** Vérifie l'état réel d'une transaction. Équivalent FlexPay "check". */
  verify(orderNumber: string): Promise<PaymentVerification>;

  /**
   * Interprète les query-params d'une URL de retour (approve/cancel/decline).
   * Retourne null si l'URL ne concerne pas ce provider.
   */
  parseReturn(params: URLSearchParams): PaymentReturn | null;
}

// ─── Configuration du module ─────────────────────────────────────────────────

/**
 * Configuration pilotée depuis le Dashboard Admin.
 * `enabled=false` → le module devient invisible : aucun écran de paiement,
 * aucun blocage, l'app fonctionne intégralement en accès libre.
 */
export interface PaymentModuleConfig {
  /** Interrupteur général du module. */
  enabled: boolean;
  /** Mode maintenance GLOBAL : écrans visibles mais tous paiements suspendus. */
  maintenanceMode: boolean;
  /** Activation individuelle des providers. */
  providers: {
    [PaymentProviderId.FLEXPAY]: boolean;
    [PaymentProviderId.MPESA]: boolean;
    [PaymentProviderId.ORANGE]: boolean;
    [PaymentProviderId.MOCK]: boolean;
  };
  /**
   * Activation individuelle de chaque OPTION de paiement (couple moyen ×
   * fournisseur), kill-switch de maintenance : couper l'une sans impacter les
   * autres. Clé = `paymentOptionKey(method, provider)`. La MÊME méthode peut
   * ainsi être coupée sur un fournisseur sans l'être sur l'autre (carte
   * bancaire via FlexPay ≠ carte bancaire via M-Pesa). Clé absente = activée.
   * Un canal dont toutes les options sont coupées affiche « paiement
   * indisponible ».
   */
  optionEnabled: Record<string, boolean>;
  /** Provider par défaut quand plusieurs sont éligibles sur un canal. */
  defaultProviderId: PaymentProviderId;
  /** Force le MockProvider partout (tests de bout en bout sans fournisseur). */
  mockMode: boolean;
  /** Délai (s) avant qu'une vérification en attente soit considérée expirée. */
  verificationTimeoutSeconds: number;
}
